import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames, { Argument } from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { b } from 'utils/i18nUtils';

import { logout } from 'modules/auth/actions';

import useCurrentStep from 'hooks/useCurrentStep';
import { getSelfServiceType } from 'modules/selfServices/selectors';
import { getOrigin, isLocalKiosk } from 'modules/dealers/selectors';

import Icon from './Icon';
import Confirm from './Confirm';
import Button, { ButtonType } from './Button';
import { StepIndicatorProps } from './StepIndicator';
import { useLocation, useNavigate } from 'react-router';

export interface StepsHeaderProps extends Partial<StepIndicatorProps> {
  onPrev?: () => void;
  hideSteps?: boolean;
  className?: Argument;
  title?: React.ReactNode;
  hidePrevButton?: boolean;
}

// eslint-disable-next-line max-len
const stepIndicator = 'before:content=[\'\'] before:absolute before:border-b-2 before:border-header-border-progress before:w-[--progress-width]';

const StepsHeader: React.FC<StepsHeaderProps> = ({
  currentIndex, length, onPrev, hideSteps, className, hidePrevButton, title,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const allowReviewCheckIn = location.state?.allowReviewCheckIn;

  const [showSignOutModal, setShowSignOutModal] = React.useState(false);

  const currentStep = useCurrentStep();
  const origin = useSelector(getOrigin);
  const isKiosk = useSelector(isLocalKiosk);
  const type = useSelector(getSelfServiceType);

  const styleAttribute = React.useMemo(
    () => ({
      '--progress-width': hideSteps ? 0 : `${((currentIndex + 1) / length) * 100}%`,
    } as React.CSSProperties),
    [currentIndex, hideSteps, length],
  );

  const handleCloseModal = React.useCallback(() => {
    setShowSignOutModal(false);
  }, []);

  const handleConfirmModal = React.useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const handleSignOut = React.useCallback(() => setShowSignOutModal(true), []);

  const handleGoToFirstStep = React.useCallback(()=>{
    // If no step is specified for accessing /checkin,
    // the router will automatically redirect to the first visible step in the check-in flow.
    navigate(`/checkin/${location.search}`);
  }, [location.search, navigate]);

  const handleClick = React.useCallback(() => {
    if (currentIndex === 0) {
      handleSignOut();
    } else if (allowReviewCheckIn) {
      handleGoToFirstStep();
    } else {
      onPrev();
    }
  }, [allowReviewCheckIn, currentIndex, handleGoToFirstStep, handleSignOut, onPrev]);

  const displayBackButton = !hidePrevButton && !isKiosk;
  const displayLogoutButton = currentIndex >= 0 && isKiosk;

  return (
    <>
      <div className={classNames('flex items-center py-2 kiosk:py-10 w-100 kiosk:flex-1', className)}>
        {displayLogoutButton && (
          <Button
            analytics={{
              category: 'Logout',
              action: `${origin} ${type} logout`,
              label: `Logout started from ${currentStep}`,
            }}
            type={ButtonType.SECONDARY}
            onClick={handleSignOut}
            testId="kiosk-prev-step"
            aria-label={intl.formatMessage({ id: 'header.title', defaultMessage: 'Log out' })}
            className="!p-7 w-auto flex-row items-center justify-center flex max-w-full btn-header"
          >
            <Icon className="fb-icon--logout-1 text-xl kiosk:text-4xl mr-2"/>
            <p className="truncate !text-xl">
              <FormattedMessage id="header.title" defaultMessage="Log out" />
            </p>
          </Button>
        )}
        {displayBackButton && (
          <Button
            analytics={currentIndex === 0 ? {
              category: 'Logout',
              action: `${origin} ${type} logout`,
              label: `Logout started from ${currentStep}`,
            } : undefined}
            type={ButtonType.SECONDARY}
            onClick={handleClick}
            testId="prev-step"
            className="size-10 kiosk:size-20 !p-2 flex items-center justify-center btn-header"
            aria-label={currentIndex === 0
              ? intl.formatMessage({ id: 'header.title', defaultMessage: 'Logout' })
              : intl.formatMessage({ id: 'steps.back', defaultMessage: 'Back' })
            }
          >
            <Icon className={classNames(
              'text-2xl kiosk:text-4xl',
              { 'fb-icon--logout-1': currentIndex === 0, 'fb-icon--arrow-line-left': currentIndex !== 0 },
            )}
            />
          </Button>
        )}
      </div>
      {title && (
        <h1 className="mx-4 flex-grow kiosk:flex-grow-0 font-bold truncate text-xl !leading-8 kiosk:!leading-l kiosk:text-4xl kiosk:flex-auto kiosk:text-center text-default">
          {title}
        </h1>
      )}

      <div
        style={styleAttribute}
        className={classNames(
          'absolute bottom-0 left-0 border-b-2 border-header-border w-full',
          { [stepIndicator]: !hideSteps },
        )}
      />

      <Confirm
        focusCancel
        open={showSignOutModal}
        onCancel={handleCloseModal}
        onConfirm={handleConfirmModal}
        cancel={<FormattedMessage id="header.cancel" defaultMessage="No, continue" />}
        confirm={<FormattedMessage id="header.confirm" defaultMessage="Yes, cancel" />}
        title={(
          <h2 className="flex justify-center items-center p-4">

            <Icon className="fb-icon--logout-1 text-2xl kiosk:text-5xl mr-3 text-default" />
            <FormattedMessage id="header.title" defaultMessage="Log out" />
          </h2>
        )}
        question={(
          <FormattedMessage
            id={`header.${type?.toLowerCase()}.backModal.content`}
            defaultMessage={`Are you sure you want to <b>cancel</b> your ${type?.toLowerCase()} ?`}
            tagName="p"
            values={{ b }}
          />
        )}
      />
    </>
  );
};

export default StepsHeader;
